<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div :class="$attrs.class">
    <div
      class="dropdown-wrapper"
      :class="{ 'dropdown-active': isOpen && !disabled }"
    >
      <div
        class="dropdown"
        :class="{ 'dropdown-open': isOpen, 'dropdown-disabled': disabled }"
        role="combobox"
        :aria-expanded="isOpen"
        :aria-label="ariaLabel"
      >
        <div
          class="dropdown-label"
          @click="toggleDropdown"
          @keydown="handleLabelKeydown"
          tabindex="0"
        >
          <input
            :id="id"
            v-if="filter && isTyping"
            v-model="filterValue"
            ref="labelInput"
            class="dropdown-label-input"
            :class="{ 'p-invalid': validation?.$error }"
            type="text"
            @input="onFilter"
            @keydown="handleKeydown"
            @click.stop
            aria-autocomplete="list"
          />
          <span v-else class="dropdown-label-text">
            {{ displayLabel || placeholder }}
          </span>
          <span
            class="select-icon icon-down"
            :class="{ 'icon-rotate': isOpen }"
          ></span>
        </div>
        <ul v-if="isOpen" class="dropdown-menu" role="listbox" :id="dropdownId">
          <template
            v-for="(group, groupIndex) in filteredGroupedOptions"
            :key="`group-${groupIndex}`"
          >
            <li v-if="group.label" class="dropdown-group-label">
              {{ group.label }}
            </li>
            <li
              v-for="option in group.options"
              :key="getOptionValue(option)"
              @click="selectOption(option)"
              class="dropdown-item"
              :class="{
                'item-selected': isSelected(option),
                'dropdown-item-child': group.label,
              }"
              role="option"
              :aria-selected="isSelected(option)"
            >
              {{ getOptionLabel(option) }}
            </li>
          </template>
          <li
            v-if="
              filteredGroupedOptions.length === 0 ||
              filteredGroupedOptions.every(
                (group) => group.options.length === 0
              )
            "
            class="dropdown-empty-message"
          >
            {{ emptyFilterMessage }}
          </li>
          <li v-if="showAddOption" class="dropdown-add-wrapper">
            <ui-button
              variant="secondary"
              size="medium"
              icon="plus-active-green"
              class="dropdown-add-button"
              @click="addOption"
            >
              Create new "{{ filterValue }}"
            </ui-button>
          </li>
        </ul>
      </div>
    </div>
    <span
      v-if="validation?.$error && validation?.required?.$invalid"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ PropName }} is required.
    </span>
    <span
      v-else-if="
        validation?.$error &&
        validation?.requiredIf &&
        validation?.requiredIf.$invalid
      "
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ validation.requiredIf.$message.replace("value", PropName) }}
    </span>
  </div>
</template>

<script>
export default {
  props: {
    id: { type: String, required: true },
    modelValue: { type: [String, Number, Array, Object], default: null },
    options: {
      type: Array,
      default: () => [],
      validator: (options) =>
        !options ||
        options.every(
          (opt) => typeof opt === "object" || typeof opt === "string"
        ),
    },
    optionLabel: { type: String, default: "label" },
    optionValue: { type: String, default: "value" },
    optionGroupLabel: { type: String, default: null },
    optionGroupChildren: { type: String, default: null },
    placeholder: { type: String, default: "Select an option" },
    filter: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    ariaLabel: { type: String, default: "Dropdown" },
    emptyFilterMessage: { type: String, default: "No results found" },
    multiple: { type: Boolean, default: false },
    validation: { type: Object, required: false },
    PropName: { type: String, default: "" },
    editable: { type: Boolean, default: false },
    allowAdditions: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: false,
      isTyping: false,
      filterValue: "",
      dropdownId: `dropdown-${this.generateUUID()}`,
      isCleared: false,
    };
  },
  computed: {
    isGrouped() {
      return (
        this.optionGroupLabel &&
        this.optionGroupChildren &&
        this.options.some(
          (opt) =>
            opt[this.optionGroupLabel] &&
            Array.isArray(opt[this.optionGroupChildren])
        )
      );
    },
    groupedOptions() {
      if (!this.isGrouped) {
        return [{ label: null, options: this.options }];
      }
      return this.options.map((group) => ({
        label: group[this.optionGroupLabel],
        options: group[this.optionGroupChildren] || [],
      }));
    },
    filteredGroupedOptions() {
      if (!this.filter || !this.filterValue) return this.groupedOptions;
      return this.groupedOptions
        .map((group) => ({
          label: group.label,
          options: group.options.filter((option) => {
            const label = this.getOptionLabel(option);
            return (
              label &&
              label.toLowerCase().includes(this.filterValue.toLowerCase())
            );
          }),
        }))
        .filter((group) => group.options.length > 0 || !group.label);
    },
    flatOptions() {
      return this.groupedOptions.reduce(
        (acc, group) => [...acc, ...group.options],
        []
      );
    },
    displayLabel() {
      if (this.multiple) {
        if (!Array.isArray(this.modelValue) || this.modelValue.length === 0)
          return "";
        return this.modelValue
          .map((val) => {
            const opt = this.flatOptions.find(
              (o) => this.getOptionValue(o) === val
            );
            if (opt) return this.getOptionLabel(opt);
            // allow-additions: a typed value with no matching option is shown as-is
            return this.allowAdditions ? val : null;
          })
          .filter(
            (label) => label !== null && label !== undefined && label !== ""
          )
          .join(", ");
      }
      if (this.modelValue === null || this.modelValue === undefined) return "";
      const selectedOption = this.flatOptions.find(
        (opt) => this.getOptionValue(opt) === this.modelValue
      );
      if (selectedOption) return this.getOptionLabel(selectedOption);
      // allow-additions: a typed value with no matching option is shown as-is
      return this.allowAdditions ? String(this.modelValue) : "";
    },
    showAddOption() {
      if (!this.allowAdditions || !this.filter) return false;
      const typed = this.filterValue.trim();
      if (!typed) return false;
      // Only offer to add when the typed text isn't already an existing option
      const alreadyExists = this.flatOptions.some(
        (opt) => this.getOptionLabel(opt).toLowerCase() === typed.toLowerCase()
      );
      return !alreadyExists;
    },
  },
  methods: {
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    toggleDropdown() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen && this.isTyping) {
        this.$nextTick(() => {
          if (this.filter) {
            this.$refs.labelInput?.focus();
          }
        });
      } else {
        this.filterValue = "";
        this.isTyping = false;
        this.isCleared = false;
      }
    },
    selectOption(option) {
      if (this.disabled) return;
      const value = this.getOptionValue(option);
      if (this.multiple) {
        const currentValue = Array.isArray(this.modelValue)
          ? [...this.modelValue]
          : [];
        const index = currentValue.indexOf(value);
        if (index > -1) {
          currentValue.splice(index, 1);
        } else {
          currentValue.push(value);
        }
        this.$emit("update:modelValue", currentValue);
      } else {
        this.$emit("update:modelValue", value);
        this.isOpen = false;
        this.isTyping = false;
        this.isCleared = false;
      }
      this.filterValue = "";
    },
    addOption() {
      if (this.disabled) return;
      const value = this.filterValue.trim();
      if (!value) return;
      if (this.multiple) {
        const currentValue = Array.isArray(this.modelValue)
          ? [...this.modelValue]
          : [];
        if (!currentValue.includes(value)) {
          currentValue.push(value);
        }
        this.$emit("update:modelValue", currentValue);
      } else {
        this.$emit("update:modelValue", value);
        this.isOpen = false;
        this.isTyping = false;
        this.isCleared = false;
      }
      // Mirror the Aurelia `add` / `add-direct` event: the raw typed text is the
      // value the parent can persist / POST.
      this.$emit("add", value);
      this.filterValue = "";
    },
    getOptionLabel(option) {
      if (typeof option === "string") return option;
      return option ? option[this.optionLabel] || "" : "";
    },
    getOptionValue(option) {
      if (typeof option === "string") return option;
      return option
        ? option[this.optionValue] !== undefined
          ? option[this.optionValue]
          : option
        : null;
    },
    isSelected(option) {
      const optionValue = this.getOptionValue(option);
      if (this.multiple) {
        return (
          Array.isArray(this.modelValue) &&
          this.modelValue.includes(optionValue)
        );
      }
      return optionValue === this.modelValue;
    },
    onFilter() {
      if (!this.isTyping) {
        this.isTyping = true;
        this.isOpen = true;
        this.$nextTick(() => {
          if (this.filter) {
            this.$refs.labelInput?.focus();
          }
        });
      }
      this.$emit("filter", this.filterValue);
    },
    handleKeydown(event) {
      if (event.key === "Enter") {
        const firstGroupWithOptions = this.filteredGroupedOptions.find(
          (group) => group.options.length > 0
        );
        if (firstGroupWithOptions) {
          this.selectOption(firstGroupWithOptions.options[0]);
        } else if (this.showAddOption) {
          this.addOption();
        }
      } else if (event.key === "Escape") {
        this.isOpen = false;
        this.isTyping = false;
        this.filterValue = "";
        this.isCleared = false;
      } else if (event.key === "Backspace" && this.editable && this.isTyping) {
        if (this.multiple) {
          this.$emit("update:modelValue", []);
        } else {
          this.$emit("update:modelValue", null);
        }
        this.filterValue = "";
        this.isTyping = false;
        this.isOpen = false;
        this.isCleared = true;
        console.log("After backspace in handleKeydown", {
          modelValue: this.modelValue,
          isCleared: this.isCleared,
        });
      }
    },
    handleLabelKeydown(event) {
      if (
        !this.isTyping &&
        (event.key.length === 1 || event.key === "Backspace")
      ) {
        event.preventDefault();
        if (event.key === "Backspace" && this.editable) {
          console.log("Backspace pressed in handleLabelKeydown", {
            multiple: this.multiple,
            modelValue: this.modelValue,
          });
          if (this.multiple) {
            this.$emit("update:modelValue", []);
          } else {
            this.$emit("update:modelValue", null);
          }
          this.isCleared = true;
          this.filterValue = "";
          this.isTyping = false;
          this.isOpen = false;
        } else {
          this.isTyping = this.filter;
          this.isOpen = this.filter;
          this.filterValue = event.key.length === 1 ? event.key : "";
        }
        this.$nextTick(() => {
          if (this.filter && this.isTyping) {
            const input = this.$refs.labelInput;
            if (input) {
              input.focus();
              event.target.blur();
            }
          }
        });
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
        this.isTyping = false;
        this.filterValue = "";
        this.isCleared = false;
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>
<style scoped>
.dropdown-wrapper {
  display: inline-block;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
}

.dropdown-wrapper:focus,
.dropdown-wrapper:active,
.dropdown-wrapper:focus-within {
  box-shadow: 0px 0px 0px 2px #ffffff, 0px 0px 0px 4px #21211f;
}

.dropdown {
  position: relative;
  width: 100%;
  font-family: "Voyado Text";
  border: 1px solid #c0c0bc;
  border-radius: 0.25rem;
  background: none;
  outline: none;
  text-overflow: ellipsis;
  white-space: nowrap;
  height: 40px;
  padding: 4px 12px 4px 12px;
  align-content: center;
  transition: border-color 0.2s;
}

.dropdown-label {
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  outline: none;
}

.dropdown-label-input {
  border: none;
  background: transparent;
  width: 100%;
  font-family: "Voyado Text";
  font-size: 14.22px;
  outline: none;
  text-overflow: ellipsis;
  line-height: 20px;
  height: 20px;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

.dropdown-label-text {
  flex: 1;
  font-size: 16px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  outline: none;
  /* line-height: 20px;
  height: 20px; */
  padding-right: 20px;
  margin: 0;
  display: inline-block;
}

.select-icon {
  position: absolute;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  transition: transform 0.2s;
}

/* .icon-rotate {
  transform: translateY(-50%) rotate(180deg);
} */

.dropdown-menu {
  position: absolute;
  top: 125%;
  outline: none;
  /* left: -5px; */
  right: 0;
  border: 1px solid #ced4da;
  border-radius: 8px;
  background: #fff;
  margin: 0px;
  padding: 0;
  list-style: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  min-width: 210px;
  width: 100%;
  box-shadow: 0 1px 1px -4px rgba(0, 0, 0, 0.06),
    0 2px 2px -4px rgba(0, 0, 0, 0.06), 0 4px 4px -4px rgba(0, 0, 0, 0.06),
    0 8px 8px -4px rgba(0, 0, 0, 0.06), 0 16px 16px -4px rgba(0, 0, 0, 0.06),
    0 32px 32px -4px rgba(0, 0, 0, 0.06), 0 0 4px 0 rgba(0, 0, 0, 0.1);
}

.dropdown-group-label {
  padding: 8px;
  font-weight: bold;
  background: #f8f9fa;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
}

.dropdown-item {
  padding: 8px 16px;
  align-content: center;
  height: 44px;
  outline: none;
  cursor: pointer;
  font-size: 14.22px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}

.dropdown-item-child {
  padding-left: 24px;
}

.dropdown-item:hover {
  background: #f0f0ef;
}

.item-selected {
  background: #d8d8d5;
}

.dropdown-item.item-selected:hover {
  background: #d8d8d5;
}

.dropdown-empty-message {
  padding: 8px;
  color: #6c757d;
  font-size: 14.22px;
}

.dropdown-add-wrapper {
  padding: 8px;
}

.dropdown-add-button {
  width: 100%;
  justify-content: flex-start;
}

.dropdown-disabled .dropdown {
  opacity: 0.6;
  cursor: not-allowed;
}

.dropdown-disabled.dropdown-active {
  border: 2px solid transparent !important; /* No black border when disabled */
  width: 100%;
}
</style>
